import Vue from 'vue';
import User from '@/models/User';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { useSessionManager } from './useSessionManager';
// import SendbirdUser from '@/models/SendbirdUser';
// import Channel from '@/models/Channel';
import Auth from '@/models/Auth';
import { useCognito, COGNITO_CONFIG, COGNITO_COOKIES_STORAGE } from './useCognito';
import Utility from '@/models/Utility';
import { updateParams } from '@/plugins/getsitecontrol';

import { useGoogleAuth } from './useGoogleAuth';

export const useAuth = () => {
  const sessionManager = useSessionManager();
  const cognito = useCognito();

  const googleAuth = useGoogleAuth();

  // Helper function to create CognitoIdentityServiceProvider cookies
  const createCognitoCookies = async (user, session) => {
    try {
      const {
        CognitoUserPool,
        CognitoUser,
        CognitoUserSession,
        CognitoIdToken,
        CognitoAccessToken,
        CognitoRefreshToken
      } = await import('amazon-cognito-identity-js');

      const cognitoUser = new CognitoUser({
        Username: user.email || user.username,
        Pool: new CognitoUserPool({ ...COGNITO_CONFIG, Storage: COGNITO_COOKIES_STORAGE }),
        Storage: COGNITO_COOKIES_STORAGE
      });

      const cognitoSession = new CognitoUserSession({
        IdToken: new CognitoIdToken({ IdToken: session.idToken.jwtToken }),
        AccessToken: new CognitoAccessToken({ AccessToken: session.accessToken.jwtToken }),
        RefreshToken: new CognitoRefreshToken({ RefreshToken: session.refreshToken.token })
      });

      cognitoUser.setSignInUserSession(cognitoSession);
      console.log('[useAuth.js] Created/updated Cognito cookies');
      return true;
    } catch (error) {
      console.error('[useAuth.js] Error creating/updating Cognito cookies:', error);
      return false;
    }
  };

  const logout = async () => {
    const firebaseAuth = getAuth();

    console.log('[useAuth.js] check firebase auth user', firebaseAuth.currentUser);

    // check firebase auth user existence, if it exists, signout from firebase
    if (firebaseAuth.currentUser) {
      await sessionManager.clearSessionToken(firebaseAuth.currentUser.accessToken);

      await signOut(firebaseAuth);
    }

    if (Auth.isAuthenticated()) {
      console.log('[useAuth.js] sign out from cognito');
      Auth.signOut();

      if (window.zE) {
        window.zE('webWidget', 'logout');
      }

      if (Vue.prototype.$amplitudeClient) {
        Vue.prototype.$amplitudeClient.reset();
      }
    }
    else {
      //Finally fallback to redirect to login page
      await Auth.redirectToLogin();
    }
  };

  const getLoginAttemptCount = () => {
    let currentLoginAttempt = localStorage.getItem('kmutt4life__login-attempt') || 0;

    currentLoginAttempt = parseInt(currentLoginAttempt);

    return currentLoginAttempt;
  };

  const isLoginAttemptLimited = () => {
    let currentLoginAttempt = getLoginAttemptCount();

    return currentLoginAttempt >= 5;
  };

  const login = async (email, password) => {
    let currentLoginAttempt = getLoginAttemptCount();

    localStorage.setItem('kmutt4life__login-attempt', currentLoginAttempt + 1);

    const { response } = await Utility.api().validateSignIn({ username: email });

    const { verified = null } = response.data;

    const { valid = false } = response.data;

    if (!valid) {
      if (response.data.code === 'E1001') {
        throw new Error('DUPLICATE_GMAIL');
        // return;
      }
    }

    try {
      const { user, session } = await cognito.signIn(
        { email, password },
        { email_verified: verified ?? false }
      );

      if (user && session) {
        Auth.setCurrentUser(user);

        Auth.setSession(session);

        if (verified == null) {
          const { jwtToken } = session.accessToken;

          await User.api().registerByToken({ token: jwtToken, password });
        }

        await loadUserData();

        initUserPlugins();
      }

      localStorage.removeItem('kmutt4life__login-attempt', 0);

      return { result: true };
    } catch (error) {
      if (error.code === 'NotAuthorizedException') {
        throw new Error('Email or password is invalid.');
      } else {
        if (Vue.prototype.$sentry) {
          Vue.prototype.$sentry.captureException(error);
        }

        throw new Error('Cannot sign in, please try again.');
      }
    }
  };

  const getCurrentFirebaseUser = () => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        getAuth(),
        (user) => {
          unsubscribe();

          resolve(user);
        },
        reject
      );
    });
  };

  const initializeAuth = async () => {
    console.log('[useAuth.js] check initialized status', Auth.isInitialized());

    if (!Auth.isInitialized()) {
      let { user = null, session = null } = await cognito.initialize();
      if (!user || !session) {
        console.log('[useAuth.js] Cognito User not found, try to initialize Google User');
        ({ user, session } = googleAuth.initialize());
      }
      if (user && session) {
        Auth.setCurrentUser(user);

        Auth.setSession(session);

        console.log('[useAuth.js] check authentication status', Auth.isAuthenticated());

        console.log('[useAuth.js] check token expire date', Auth.getExpireDateAppToken());

        await loadUserData();

        initUserPlugins();
      }

      Auth.initialized();
    }
  };

  const loadUserData = async () => {
    let currentUser = Auth.getCurrentUser();

    if (!currentUser) {
      return;
    }

    await User.api().fetchAuth();

    _syncCognitoUserAttributes();
  };

  const _syncCognitoUserAttributes = async () => {
    const appUser = User.query().first();
    const cognitoUser = Auth.getCurrentUser();
    console.log('[useAuth.js] check app user', appUser);
    console.log('[useAuth.js] check cognito user', cognitoUser);
    if (appUser.isGoogleUser()) {
      return;
    }

    cognitoUser.getUserData(async (error, userData) => {
      if (error) throw new Error('Cannot get user data.');

      let updatedAttributes = {};

      const firstNameAttribute = userData.UserAttributes.find(
        (attribute) => attribute.Name === 'given_name'
      );

      const lastNameAttribute = userData.UserAttributes.find(
        (attribute) => attribute.Name === 'family_name'
      );

      if (appUser.firstname !== firstNameAttribute.Value) {
        updatedAttributes.firstname = appUser.firstname;
      }

      if (appUser.lastname !== lastNameAttribute.Value) {
        updatedAttributes.lastname = appUser.lastname;
      }

      if (Object.keys(updatedAttributes).length > 0) {
        console.log(`[useAuth.js] profile mismatch, update cognito profile.`, updatedAttributes);
        await cognito.updateProfile(updatedAttributes);
      }
    });
  };

  const initUserPlugins = async () => {
    const user = User.query().first();

    if (user) {
      // if (!SendbirdUser.isConnected(user.id)) {
      //   await SendbirdUser.api().userConnect(user);
      //   SendbirdUser.getTotalUnreadMessageCount(user.id);
      //   Channel.registerEventHandlers(user.id);
      // }

      if (Vue.prototype.$logrocket) {
        Vue.prototype.$logrocket.init(process.env.VUE_APP_LOG_ROCKET_PROJECT);

        Vue.prototype.$logrocket.getSessionURL((sessionURL) => {
          if (Vue.$sentry) {
            Vue.$sentry.configureScope((scope) => {
              scope.setExtra('sessionURL', sessionURL);
            });
          }

          if (Vue.$amplitudeClient) {
            Vue.$amplitudeClient.track('LogRocket', { sessionURL });
          }
        });

        Vue.prototype.$logrocket.identify(user.email, {
          name: `${user.firstname} ${user.lastname}`,
          email: user.email,
          applicationId: user.id,
          // firebaseId: user.uid,
          globalRoles: user.user_roles.map(({ role }) => role).join(',')
        });
      }

      if (Vue.prototype.$amplitudeClient) {
        const identifyEvent = new Vue.prototype.$amplitudeClient.Identify();

        Vue.prototype.$amplitudeClient.setUserId(user.email);

        identifyEvent.set('Name', `${user.firstname} ${user.lastname}`);
        identifyEvent.set('Application ID', user.id);
        // identifyEvent.set('Firebase ID', user.uid);
        identifyEvent.set(
          'Global Roles',
          user.user_roles.map(({ role }) => role)
        );

        Vue.prototype.$amplitudeClient.identify(identifyEvent);
      }

      if (Vue.prototype.$sentry) {
        Vue.prototype.$sentry.setUser({
          id: user.id,
          username: user.username,
          email: user.email
        });
      }

      if (window.zE) {
        window.zE('webWidget', 'prefill', {
          name: { value: `${user.firstname} ${user.lastname}` },
          email: { value: user.email }
        });

        window.zE('webWidget', 'identify', {
          name: `${user.firstname} ${user.lastname}`,
          email: user.email
        });
      }

      if (window.gsc) {
        updateParams({
          user_id: user.id,
          user_email: user.email,
          user_name: `${user.firstname} ${user.lastname}`,
          user_global_roles: user.user_roles.map(({ role }) => role)
        });
      }
    }
  };

  const refreshToken = async () => {
    const appUser = User.query().first();
    let user;
    let session;
    if (appUser) {
      console.log('[useAuth.js] refresh token with cognito');

      ({ user, session } = await cognito.refresh(Auth.getRefreshToken()));

      if (user && session) {
        console.log('[useAuth.js] refresh token success', { user, session });

        Auth.setCurrentUser(user);
        Auth.setSession(session);
      }
      else {
        console.log('[useAuth.js] refresh token failed', { user, session });
        throw new Error('Cannot refresh token');
      }
    } 
  };

  const handleErrorInvalidToken = async (error) => {
    if (error.response?.data?.statusCode === 401 || error.response?.status === 401) {
      const isUserNotFound = error.response?.data?.message?.includes('entry.notFound');
      const isInvalidToken = error.response?.data?.message?.includes(
        'Access Token has been revoked.'
      );

      const firebaseUser = await getCurrentFirebaseUser();

      if (firebaseUser || isUserNotFound) {
        Auth.markTokenInvalid();

        return Promise.reject(error);
      } else if (isInvalidToken) {
        Auth.markTokenInvalid();
        // await logout();

        return Promise.reject(error);
      } else {
        try {
          await refreshToken();

          return Promise.resolve();
        } catch (error) {
          console.log('[useAuth.js] refresh token error', { ...error });

          if (error.code === 'NotAuthorizedException') {
            Auth.markTokenInvalid();
          }

          return Promise.reject(error);
        }
      }
    }

    return Promise.reject(error);
  };

  const checkPassword = async (email, password) => {
    const { response } = await Utility.api().validateSignIn({ username: email });

    const { verified = null } = response.data;

    try {
      const { user, session } = await cognito.signIn(
        { email, password },
        { email_verified: verified ?? false }
      );
      if (user && session) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  };
  //AKA signin with google
  const googleAuthen = async (code) => {
    try {
      const {
        response: { data }
      } = await Utility.api().googleAuthenticate(code);

      const { user, session } = data;

      if (user && session) {
        user.signOut = () => {
          googleAuth.signOut();
        };

        Auth.setCurrentUser(user);
        Auth.setSession(session);

        // Store in localStorage (existing functionality)
        // googleAuth.setSession(session);
        // googleAuth.setUser(user);

        // Create CognitoIdentityServiceProvider cookies by using the Cognito SDK
        try {
          await createCognitoCookies(user, session);
        } catch (error) {
          console.error('[useAuth.js] Error setting Cognito cookies for Google user:', error);
        }

        await loadUserData();
        initUserPlugins();
      }
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  return {
    login,
    logout,
    initializeAuth,
    isLoginAttemptLimited,
    loadUserData,
    initUserPlugins,
    getLoginAttemptCount,
    refreshToken,
    getCurrentFirebaseUser,
    handleErrorInvalidToken,
    checkPassword,
    googleAuthen
  };
};
